/** @format */

import React, { FunctionComponent, ReactElement, useEffect, useState } from 'react';
import GridContainer from '../../components/Grid/GridContainer';
import Filter from '../../interfaces/forms/Filter';
import Order from '../../interfaces/forms/Order';
import { useDispatch, useSelector } from 'react-redux';
import { FilterReducerFieldName, FilterReducers } from '../../modules/filter/FilterReducers';
import GridItem from '../../components/Grid/GridItem';
import PaperedTable from '../../components/Table/PaperedTable';
import { TableBody, TableFooter, TableHead, TableRow, withStyles } from '@material-ui/core';
import TableCell from '@material-ui/core/TableCell';
import CustomTextButtom from '../../components/Buttons/CustomTextButton';
import FilterList from '../../components/Filter/FilterList';
import TableHeaderContentCell from '../../components/Table/TableHeaderContentCell';
import Pager from '../../components/Adescom/PagerRange';
import { FilterListReducerFieldName } from '../../modules/filter/FilterListReducers';
import { history } from '../../core/history';
import { FormGeneralHelper } from '../../helpers/FormGeneral';
import { FeaturesServiceReducerType, ServicesAction } from '../../modules/features/FeaturesReducers';
import { ItemLoaders } from '../../core/loaders/ItemLoaders';
import { FilterManager } from '../../modules/filter/FilterManager';
import { SimCardServiceTemplateCollection } from '../../interfaces/platform/SimCardServiceTemplate';
import { ServiceTemplateItem } from '../../interfaces/platform/ServiceTemplateItem';
import FabAddButton from '../../components/Buttons/FabAddButton';
import { roles, UserRoleHelper } from '../../helpers/UserRoleHelper';
import { ServiceTemplateAddNew } from './ServiceTemplateAddNew';
import oplAppStyle from '../../assets/themes/OPL/jss/App/OplAppStyle';

type ServiceTemplateListType = {
    classes: any;
    showMainLoader(show: boolean): void;
};

const setDefaultFilters = (): Array<Filter> => [
    { name: 'systemRestrictedFlag', value: false },
    { name: 'clip', value: false },
];
const setDefaultOrders = (): Array<Order> => [{ name: 'templateName', value: 'asc' }];

const ServiceTemplateList: FunctionComponent<ServiceTemplateListType> = ({ classes, showMainLoader }): ReactElement => {
    const dispatch = useDispatch();
    const { headerFilterElementHasValue, headerFilterElementName } = useSelector((state: { filterReducer: FilterReducers }) => state.filterReducer);
    const [filters, setFilters] = useState<Array<Filter>>(setDefaultFilters());
    const [orders, setOrders] = useState<Array<Order>>(setDefaultOrders());
    const [activeRow, setActiveRow] = useState(-1);
    const [reload, setReload] = useState(false);
    const [newTemplate, setNewTemplate] = useState(false);
    const [showClearFilterButton, setShowClearFilterButton] = useState(false);
    const { serviceTemplateList } = useSelector((state: { serviceReducer: FeaturesServiceReducerType }) => state.serviceReducer);

    const clearFilter = (): void => {
        setFilters(setDefaultFilters());
        setOrders(setDefaultOrders());
        setShowClearFilterButton(false);
        dispatch({
            type: FilterReducerFieldName.HEADER_FILTER_MANAGE,
            clearAll: true,
        });
        dispatch({
            type: FilterListReducerFieldName.HEADER_FILTER_MANAGE,
        });
    };

    useEffect(() => {
        const filterManage = new FilterManager();
        if (headerFilterElementName) {
            if (headerFilterElementHasValue) {
                filterManage.manageElement(headerFilterElementName, headerFilterElementHasValue);
            } else {
                filterManage.manageElement(headerFilterElementName, false);
            }
            setShowClearFilterButton(filterManage.hasManageElement());
        }
    }, [headerFilterElementName, headerFilterElementHasValue, dispatch]);

    useEffect(() => {
        setReload(true);
    }, [orders]);

    const updateOrderList = (orders: Array<Order>): void => setOrders(orders);
    const updateFilterList = (filters: Array<Filter>): void => setFilters(filters);
    const firstLoad = (): void => {};

    const viewCallback = (iri: { uri: string }, event: any): void => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
        // @ts-ignore
        history.respectModifierPush(event, FormGeneralHelper.getRouteUrl('/service-template-edit' + iri.uri));
    };

    const deleteCallback = (iri: { uri: string }, event: any): void => {};

    const paginationCallbackFunction = (uri: string): void => {
        showMainLoader(true);
        const itemLoader = new ItemLoaders(dispatch);
        itemLoader.paginationCallWithCallback(
            uri,
            'sim_card_services_types_list_load',
            (data: SimCardServiceTemplateCollection): void => {
                dispatch({
                    type: ServicesAction.FEATURES_LOAD_SIM_CARD_SERVICE_TEMPLATE_LIST,
                    serviceTemplateList: data,
                } as FeaturesServiceReducerType);
                showMainLoader(false);
            },
            { propagateDataToCallback: true, dispatchAfterCallback: false }
        );
    };

    useEffect(() => {
        if (reload) {
            setReload(false);
            showMainLoader(true);
            const itemLoader = new ItemLoaders(dispatch);
            itemLoader.getListWithCallback(
                FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/sim_card_service_templates')),
                'sim_card_services_types_list_load',
                filters,
                orders,
                (data: SimCardServiceTemplateCollection) => {
                    dispatch({
                        type: FilterListReducerFieldName.HEADER_FILTER_MANAGE,
                        showRender: true,
                    });
                    dispatch({
                        type: ServicesAction.FEATURES_LOAD_SIM_CARD_SERVICE_TEMPLATE_LIST,
                        serviceTemplateList: data,
                    } as FeaturesServiceReducerType);
                    showMainLoader(false);
                },
                { propagateDataToCallback: true, dispatchAfterCallback: false }
            );
        }
    }, [dispatch, filters, orders, reload, showMainLoader]);

    const addNewTemplate = (): void => setNewTemplate(true);

    const tableStickyHeader = { root: classes.tableStickyHeader };
    const tableControlStickyHeader = { root: classes.tableControlStickyHeader };
    const tableStickyCommand = { root: classes.tableStickyCommand };
    return (
        <GridContainer>
            <GridItem xs={12} md={12} lg={12}>
                <PaperedTable
                    beforeTableContent={
                        <GridContainer>
                            <GridItem xs={12} className={classes.beforeTableContent}>
                                <CustomTextButtom
                                    callback={(): void => {
                                        showMainLoader(true);
                                        setReload(true);
                                    }}
                                    callbackParam={{}}
                                    color="primary"
                                    label="general:common.search"
                                    size="lg"
                                />
                                {showClearFilterButton && (
                                    <CustomTextButtom
                                        callback={clearFilter}
                                        callbackParam={{}}
                                        color="primary"
                                        label="general:common.clear"
                                        size="lg"
                                    />
                                )}
                                <FilterList />
                            </GridItem>
                        </GridContainer>
                    }
                >
                    <TableHead>
                        <TableRow>
                            <TableCell classes={tableStickyCommand} padding="none" align="left">
                                <TableHeaderContentCell textAlign="left" title="general:common.commands" onlyFilter={true} />
                            </TableCell>
                            <TableCell classes={tableStickyHeader} padding="none" align="left">
                                <TableHeaderContentCell
                                    title="prov:features.identity_number"
                                    textAlign="left"
                                    fieldName="id"
                                    showSort={true}
                                    onlyFilter={false}
                                    defaultDirection="desc"
                                    searchFieldType="number"
                                    filters={filters}
                                    sort={orders}
                                    updateOrderList={updateOrderList}
                                    updateFilterList={updateFilterList}
                                    searchActionCallback={firstLoad}
                                />
                            </TableCell>
                            <TableCell classes={tableStickyHeader} padding="none" align="left">
                                <TableHeaderContentCell
                                    title="prov:features.template_name"
                                    textAlign="left"
                                    fieldName="templateName"
                                    showSort={true}
                                    onlyFilter={false}
                                    defaultDirection="desc"
                                    searchFieldType="string"
                                    filters={filters}
                                    sort={orders}
                                    updateOrderList={updateOrderList}
                                    updateFilterList={updateFilterList}
                                    searchActionCallback={firstLoad}
                                />
                            </TableCell>
                            <TableCell classes={tableStickyHeader} align="left">
                                <TableHeaderContentCell
                                    textAlign="left"
                                    title="prov:features.is_active"
                                    updateOrderList={updateOrderList}
                                    updateFilterList={updateFilterList}
                                    fieldName="active"
                                    showSort={true}
                                    searchFieldType="select"
                                    selectComponent="headerBooleanCheckBoxSelectList"
                                    filters={filters}
                                    sort={orders}
                                />
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {serviceTemplateList &&
                            serviceTemplateList['hydra:member'].map((listItem) => {
                                return (
                                    <ServiceTemplateItem
                                        classes={classes}
                                        key={'scst_' + listItem.id}
                                        item={listItem}
                                        activeRow={activeRow}
                                        setActiveRow={setActiveRow}
                                        viewCallback={viewCallback}
                                        deleteCallback={deleteCallback}
                                    />
                                );
                            })}
                    </TableBody>
                    <TableFooter>
                        <TableRow>
                            <TableCell colSpan={10}>
                                {serviceTemplateList && serviceTemplateList['hydra:view'] && (
                                    <Pager callback={paginationCallbackFunction} views={serviceTemplateList} />
                                )}
                            </TableCell>
                        </TableRow>
                    </TableFooter>
                </PaperedTable>
                {UserRoleHelper.checkRole(roles.ROLE_SIM_CARD_SERVICE_TEMPLATE_WRITE) && (
                    <React.Fragment>
                        <FabAddButton callback={addNewTemplate} />
                        <ServiceTemplateAddNew
                            open={newTemplate}
                            closeCallback={(): void => setNewTemplate(false)}
                            reloadCallback={(): void => setReload(true)}
                        />
                    </React.Fragment>
                )}
            </GridItem>
        </GridContainer>
    );
};

export default withStyles(oplAppStyle)(ServiceTemplateList);
